import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signOut,
} from 'aws-amplify/auth';
import store from '../store/store';
import { logout } from '../reducers/authSlice';
import userApiService from '../services/oraganisation-api.service';

/**
 * Initialize the authentication state and return user session data.
 * Fetches org+subscription in a single call to GET /get_org_admin_details.
 * @returns {Promise<{user, token, isAuthenticated, userAttributes, organization, organizationError} | null>}
 */
export const initializeAuthStore = async () => {
  try {
    const session = await fetchAuthSession();
    const sessionToken = session.tokens?.idToken?.toString();

    if (!sessionToken) {
      return null;
    }

    const user = await getCurrentUser();
    const userAttributes = await fetchUserAttributes();

    const userRole = userAttributes['custom:role'];

    let organization = null;
    let organizationError = null;

    if (userRole !== 'super_admin') {
      try {
        const orgData = await userApiService.getOrgDetails();
        organization = orgData;
      } catch (err) {
        console.error('Error fetching organization details:', err);
        organizationError = 'Failed to load organization details. Please refresh the page.';
      }
    }

    return {
      user,
      token: sessionToken,
      isAuthenticated: true,
      userAttributes,
      organization,
      organizationError,
    };
  } catch (error) {
    await signOut();
    store.dispatch(logout());
    console.error('Error initializing auth store:', error);
  }
};

/**
 * Check if user has an active session.
 * @returns {Promise<boolean>}
 */
export const hasActiveSession = async () => {
  try {
    const session = await fetchAuthSession();
    return !!session.tokens?.idToken;
  } catch {
    return false;
  }
};

/**
 * Refresh user attributes.
 * @returns {Promise<Object>}
 */
export const refreshUserAttributes = async () => {
  try {
    return await fetchUserAttributes();
  } catch (error) {
    console.error('Error refreshing user attributes:', error);
    throw error;
  }
};
